import { getService } from "react-in-angularjs";

import {
    declareAiCapability,
    provideAiCapability,
} from "@medflyt/webapp-react/src/modules/ai-omnibar/ai-capability-registry";

// The claims page describing its own date range to the AI omnibar. `scope: "page"` means it is
// reachable once the user is on a page - app.navigate declares that it opens that space - and the
// description says which page, so "last month's claims" becomes navigate + filter rather than
// navigate and nothing.
declareAiCapability({
    id: "invoices.filter",
    title: "Filter the claims list",
    effect: "prefill",
    description:
        "Sets the filters on the Billing > Claims page and re-runs its search: the date range and whether that range means when the claim was created or when the service happened, plus contract, office, service code, export type, export status, payment status, billable/pending, manual hold, check line status and the invoice / invoice batch / export batch ids. Every filter left out keeps whatever the page already has, so this can narrow a search the user already ran. Only works while that page is open.",
    location: "the filter bar of the Billing > Claims page",
    scope: "page",
    permissionKey: "view_billing_section_invoices",
    params: [
        {
            name: "dateBasis",
            type: "enum",
            required: false,
            description:
                "Which date the range applies to. Use service-time when the user talks about visit or service dates, invoice-creation when they talk about when the claim or invoice was made. Defaults to whatever the page already has.",
            options: [
                { value: "invoice-creation", label: "Invoice creation date" },
                { value: "service-time", label: "Service / visit date" }
            ]
        },
        {
            name: "from",
            type: "date",
            required: false,
            description: "First day to include, as yyyy-MM-dd."
        },
        {
            name: "to",
            type: "date",
            required: false,
            description: "Last day to include, as yyyy-MM-dd."
        },
        {
            name: "contract",
            type: "enum",
            required: false,
            multiple: true,
            // Agency data, so the list has to be read when the manifest is built rather than when
            // this file is imported.
            options: () => getInvoiceContractOptions(),
            description: "Limit to one contract."
        },
        {
            name: "office",
            type: "enum",
            required: false,
            multiple: true,
            options: () => getInvoiceOfficeOptions(),
            description: "Limit to one office."
        },
        {
            name: "serviceCode",
            type: "enum",
            required: false,
            multiple: true,
            options: () => getInvoiceServiceCodeOptions(),
            description: "Limit to one service code."
        },
        {
            name: "exportType",
            type: "enum",
            required: false,
            options: () => getInvoiceExportTypeOptions(),
            description: "Limit to claims exported through one e-billing provider."
        },
        {
            name: "exportStatus",
            type: "enum",
            required: false,
            multiple: true,
            description: "Whether the claim has been exported.",
            options: [
                { value: "FULL", label: "Exported" },
                { value: "NONE", label: "Not exported" },
                { value: "PARTIAL", label: "Partially exported" },
                { value: "ERROR", label: "Error" }
            ]
        },
        {
            name: "paymentStatus",
            type: "enum",
            required: false,
            multiple: true,
            description: "How much of the claim has been paid.",
            options: [
                { value: "FULL", label: "Paid" },
                { value: "PARTIAL", label: "Partially paid" },
                { value: "NONE", label: "Not paid" },
                { value: "OVER", label: "Over paid" }
            ]
        },
        {
            name: "billType",
            type: "enum",
            required: false,
            description: "Billable claims or ones still pending.",
            options: [
                { value: "false", label: "Billable" },
                { value: "true", label: "Pending" }
            ]
        },
        {
            name: "manualHold",
            type: "enum",
            required: false,
            description: "Whether invoicing is being held by hand.",
            options: [
                { value: "true", label: "Held" },
                { value: "false", label: "Not held" }
            ]
        },
        {
            name: "checkLineStatus",
            type: "enum",
            required: false,
            description:
                "Whether the claim has a check line. Only exists in the Service view of this page.",
            options: [
                { value: "true", label: "Has check line" },
                { value: "false", label: "Missing check line" }
            ]
        },
        {
            name: "textSearch",
            type: "text",
            required: false,
            description:
                "Types into the page's own search box, which narrows the claims already listed. For what no other filter can hold - a patient name, a payer, a note. A contract, office or service code belongs in its own filter, not here.",
            // Where a word that is in none of this screen's lists ends up, rather than the step
            // failing. The browser moves it to a real filter first if one holds that exact name.
            searchesFreeText: true
        },
        {
            name: "invoiceId",
            type: "text",
            required: false,
            description: "One invoice number."
        },
        {
            name: "invoiceBatchId",
            type: "text",
            required: false,
            description: "One invoice batch number."
        },
        {
            name: "exportBatchId",
            type: "text",
            required: false,
            description: "One export batch number."
        }
    ],
    examples: [
        "show me claims with visit dates in the past month",
        "unpaid claims for last month",
        "claims not exported yet",
        "only the Brooklyn office"
    ]
});

/**
 * The agency's contracts, as options. Read through the injector rather than the controller's own
 * injections because this runs at manifest time, which may be long before this page is open.
 */
function getInvoiceContractOptions() {
    try {
        const contractTypes = getService("DatabaseApi").contractTypes() ?? [];

        return contractTypes.map((contractType) => ({
            value: String(contractType.id),
            label: contractType.name
        }));
    } catch (error) {
        return [];
    }
}

/** The offices this agency still uses, as options. Same manifest-time caveat as the contracts. */
function getInvoiceOfficeOptions() {
    try {
        return (getService("DatabaseApi").activeOffices() ?? []).map((office) => ({
            value: String(office.id),
            label: office.name
        }));
    } catch (error) {
        return [];
    }
}

/** Active service codes, labelled by their code the way the page's own dropdown labels them. */
function getInvoiceServiceCodeOptions() {
    try {
        return (getService("DatabaseApi").serviceCodes() ?? [])
            .filter((serviceCode) => serviceCode.isActive)
            .map((serviceCode) => ({
                value: String(serviceCode.id),
                label: serviceCode.code
            }));
    } catch (error) {
        return [];
    }
}

function getInvoiceExportTypeOptions() {
    try {
        return (getService("DatabaseApi").eBillingProviders() ?? []).map((provider) => ({
            value: String(provider),
            label: String(provider)
        }));
    } catch (error) {
        return [];
    }
}

/** yyyy-MM-dd -> the local Date the page manager's date params hold. */
function parseInvoicesFilterDate(value) {
    const [year, month, day] = String(value).split("-").map(Number);

    return new Date(year, month - 1, day);
}
//! @ngInject
export function billingInvoicesCtrl($scope, $rootScope, $timeout, NgTableParams, $state, itemSearchPageManager, selectionLogic, toaster, billingInvoicesProcessor, DatabaseApi, $filter, FilterUtils, SweetAlert) {
    const universalBOM = "\uFEFF";
    $scope.offices = DatabaseApi.offices();
    $scope.tableView = {
        invoice: {
            tableParams: null,
            serviceCount: 0,
            invoiceCount: 0,
            allItems: []
        },
        service: {
            tableParams: null,
            serviceCount: 0,
            invoiceCount: 0,
            allItems: []
        }
    };
    $scope.openPatientModal = $rootScope.openPatientModal;
    $scope.globalFilter = { val: '' };

    $scope.eBillingProviderOptions = DatabaseApi.eBillingProviders().map(item => ({
        id: item, label: item
    }));

    $scope.viewTypeIndex = 0;
    $scope.viewType = "Invoice";

    $scope.missingTableData = function () {
        const tableView = $scope.viewType === "Invoice" ? $scope.tableView.invoice : $scope.tableView.service;

        return !tableView.tableParams || !tableView.tableParams.data || !tableView.tableParams.data.length
    }

    $scope.changeViewType = function (viewType) {
        $scope.viewType = viewType;
        switch (viewType) {
            case "Invoice":
                $scope.pageManager.updateRelativeURL("/invoices");
                $scope.arTypeIndex = 0;
                initSelection($scope.tableView.invoice.allItems, viewType);
                break;
            case "Service":
                $scope.pageManager.updateRelativeURL("/invoices_by_service");
                $scope.arTypeIndex = 1;
                initSelection($scope.tableView.service.allItems, viewType);
                break;
        }
    }

    $scope.exportStatusOptions = [{
        id: "FULL", label: "Exported"
    },{
        id: "NONE", label: "Not exported"
    },{
        id: "PARTIAL", label: "Partially exported"
    },{
        id: "ERROR", label: "Error"
    }];

    $scope.billTypeOptions = [{
        id: "false", label: "Billable"
    },{
        id: "true", label: "Pending"
    }];

    $scope.serviceTypeOptions = [{
        id: "DIRECT_CARE", label: "Direct Care"
    },{
        id: "ADMINISTRATIVE_PAY", label: "Administrative Payment"
    }];

    $scope.paymentStatus = [{
        id: "FULL", label: "Paid"
    },{
        id: "PARTIAL", label: "Partially Paid"
    },{
        id: "NONE", label: "Not Paid"
    },{
        id: "OVER", label: "Over Paid"
    }];

    $scope.manualHoldOptions = [{
        id: "true", label: "Held"
    },{
        id: "false", label: "Not Held"
    }];

    $scope.hasCheckLineOptions = [{
        id: "true", label: "Has Check Line"
    },{
        id: "false", label: "Missing Check Line"
    }];

    $scope.selectedEBillingProviders = [];
    $scope.selectedExportStatuses = [];
    $scope.selectedPaymentStatuses = [];
    $scope.selectedManualHold = [];
    $scope.selectedHasCheckLine = [];
    $scope.selectedBillType = [{id: "false", label: "Billable"}];
    $scope.selectedServices = [];

    function initialize() {
        initPageManager();
        initSelection([], $scope.viewType);
        $scope.pageManager.resetSearchParamsByUrl();

        // Load filters
        const storageFilters = FilterUtils.rootFilters.billingInvoicesCtrl;
        if (storageFilters !== undefined) {
            $scope.pageManager.setSearchParams(storageFilters);
        }
        $scope.selectedEntity = {
            type: '',
            id: ''
        };
    }

    $scope.$on("$destroy", function () {
        const filters = $scope.pageManager.searchParams;
        FilterUtils.rootFilters.billingInvoicesCtrl = angular.copy(filters);
    });

    const initPageManager = function () {
        $scope.pageManager = itemSearchPageManager.createSearchPageManager("/invoices");
        $scope.changeViewType("Invoice");

        $scope.officeDataManager = $scope.pageManager.getOfficeDataManager();
        $scope.contractTypesDataManager = $scope.pageManager.getContractTypesDataManager();
        $scope.serviceCodesDataManager = $scope.pageManager.getServiceCodesDataManager();

        $scope.pageManager.initFromToDateParams();
        $scope.pageManager.initSearchParam("invoiceId", "", {
            isEntity: true,
        });
        $scope.pageManager.initSearchParam("exportBatchId", "", {
            isEntity: true,
        });
        $scope.pageManager.initSearchParam("selectedPatientId", null);
        $scope.pageManager.initContractTypesParam();
        $scope.pageManager.initServiceCodesParam();
        $scope.pageManager.initOfficeParam();
        $scope.pageManager.initSearchParamDeprecated("exportStatus", "", "exportStatuses", undefined, undefined, "");
        $scope.pageManager.initSearchParamDeprecated("paymentStatus", "", "paymentStatuses", undefined, undefined, "");
        $scope.pageManager.initSearchParamDeprecated("upToDate", "true", "up-to-date", undefined, undefined, true);
        $scope.pageManager.initSearchParamDeprecated("billType", "false", "pending", undefined, undefined, "");
        $scope.pageManager.initSearchParamDeprecated("manualHold", "", "hold", undefined, undefined, "");
        $scope.pageManager.initSearchParamDeprecated("eBillingProvider", "", "e-billing-provider", undefined, undefined, "");
        $scope.pageManager.initSearchParamDeprecated("hasCheckLine", "", "hasCheckLine", undefined, undefined, "");
        $scope.pageManager.initSearchParamDeprecated("serviceType", "", "serviceType", undefined, undefined, "");
        $scope.pageManager.updateSearchParamValue("upToDate", true);
        $scope.pageManager.initSearchParam(
            "invoiceBatchId",
            $state.params.batchId ? $state.params.batchId : "",
            { isEntity: true }
        );
        $scope.pageManager.initSearchParam("dateByInvoice", true, {
            placeholderValue: true,
            queryName: "date-by-invoice",
        });
        $scope.pageManager.initSearchParam("showDeleted", true, {
            placeholderValue: false,
            queryName: "show-deleted-invoices",
        });
    };

    const initSelection = function (items, type) {
        $scope.selectionLogic = selectionLogic.createNewLogic(function (item) {
            if (!item.exported) $scope.selectionLogic.addItemToCollection(item);
        }, "id");
        if (items) {
            items.forEach((item) => {
                $scope.selectionLogic.initItem(item);
                item.serviceType = item.invoiceVisitId !== undefined ? "Direct Care" : "Administative Payment";
            });
        }

        const tableParamsHolder = type === "Invoice" ? $scope.tableView.invoice : $scope.tableView.service;

        tableParamsHolder.serviceCount = type === "Service" ? items.length : items.reduce(function (a, b) {
            return a + b.serviceCount;
        }, 0);
        tableParamsHolder.invoiceCount = type === "Service" ? (new Set(items.map(row => row.invoiceId))).size : items.length;
    };

    const mapItems = function (items) {
        items.forEach(function (item) {
            if (!item.contractTypeName && item.contractTypeId) {
                const contractType = $scope.contractTypesDataManager.getContractTypeById(item.contractTypeId);
                item.contractTypeName = (contractType && contractType.name) || item.contractTypeId;
            }

            //should receive the 'exported' indication from the server
            // Doesn't work and hasn't worked for a while (exportBatchId).
            // item.exported = type === 'Invoice' ? Boolean(item.exportBatchId) : item.exportedStatus === "FULL";

            // $scope.selectionLogic.initItem(item);
        });
    };

    const initTable = function (items, type) {
        const officeNameMap = new Map(DatabaseApi.offices().map(office => [office.id, office.name]));
        initSelection(items, type);
        const serviceCodesMap = new Map(DatabaseApi.serviceCodes().map(item => [item.id, item.code]));
        items.forEach(function (item) {
            if (item.officeId) {
                item.officeName = officeNameMap.get(item.officeId);
            }
            if (item.serviceCodeIds) {
                item.serviceCodes = item.serviceCodeIds.map(id => serviceCodesMap.get(id)).filter(code => code).join();
            }
            if (item.serviceCodeId) {
                item.serviceCode = serviceCodesMap.get(item.serviceCodeId);
            }
        });
        const hasItems = items && items.length;
        if (hasItems) {
            mapItems(items, type);
            // toaster.pop("success", items.length + " Invoices were loaded successfully");
        }

        const options = {
            count: 25
        };

        const tableParamsHolder = type === "Invoice" ? $scope.tableView.invoice : $scope.tableView.service;

        tableParamsHolder.tableParams = new NgTableParams(options, {
            dataset: items
        });
    };

    function setAllItemsByType(response, type) {
        if (type === "Invoice") {
            const items = response.data.invoices;
            $scope.tableView.invoice.allItems = [...items];
            return response.data.invoices;
        }

        if (type === "Service") {
            const items = response.data.invoicesViewByService;
            $scope.tableView.service.allItems = [...items];
            return items;
        }

        return null;
    }

    $scope.loadItems = function () {
        const type = $scope.viewType;

        $scope.globalFilter.val = '';
        $scope.pageManager.searchParams.eBillingProvider.value = $scope.selectedEBillingProviders.map(eBillingProvider => eBillingProvider.id).join("|");
        $scope.pageManager.searchParams.exportStatus.value = $scope.selectedExportStatuses.map(item => item.id).join("|");
        $scope.pageManager.searchParams.paymentStatus.value = $scope.selectedPaymentStatuses.map(item => item.id).join("|");
        $scope.pageManager.searchParams.billType.value = $scope.selectedBillType.length === 1 ? $scope.selectedBillType[0].id : "";
        $scope.pageManager.searchParams.manualHold.value = $scope.selectedManualHold.length === 1 ? $scope.selectedManualHold[0].id : "";
        $scope.pageManager.searchParams.hasCheckLine.value = $scope.selectedHasCheckLine.length === 1 ? $scope.selectedHasCheckLine[0].id : "";
        $scope.pageManager.searchParams.serviceType.value = $scope.selectedServices.map(item => item.id).join("|");

        // When searching by invoice ID, force showing deleted invoices so the result isn't filtered out
        const prevShowDeletedPlaceholder = $scope.pageManager.searchParams.showDeleted.placeholder;
        if ($scope.pageManager.searchParams.invoiceId.value) {
            $scope.pageManager.searchParams.showDeleted.placeholder = true;
        }

        // Returned so a caller can act once the rows are in - loadItems clears the global search
        // box on the way past, so anything typed there has to be re-applied afterwards.
        return $scope.pageManager.executeSearch().then(function (response) {
            $scope.pageManager.searchParams.showDeleted.placeholder = prevShowDeletedPlaceholder;
            const items = setAllItemsByType(response, type);

            if (!items) {
                throw new Error("Unknown view type");
            }

            initTable(items, type);
        }, function (error) {
            $scope.pageManager.searchParams.showDeleted.placeholder = prevShowDeletedPlaceholder;
            toaster.pop("error", "Failed to load invoices");
        });
    };

    // Available only while this page is on screen.
    const releaseInvoicesFilterCapability = provideAiCapability("invoices.filter", (args) => {
        return $timeout(() => {
            // Checked before anything is applied: a step that cannot run should leave the page as it
            // was, not half-filtered with the old results still on screen.
            if (args.checkLineStatus !== undefined && $scope.viewType !== "Service") {
                throw new Error(
                    "Check line status only applies to the Service view of the claims page."
                );
            }

            const applied = [];

            if (args.dateBasis !== undefined) {
                const byService = String(args.dateBasis) === "service-time";
                $scope.pageManager.searchParams.dateByInvoice.value = !byService;
                applied.push(byService ? "by service date" : "by invoice date");
            }

            if (args.from !== undefined) {
                $scope.pageManager.searchParams.from.value = parseInvoicesFilterDate(args.from);
            }

            if (args.to !== undefined) {
                $scope.pageManager.searchParams.to.value = parseInvoicesFilterDate(args.to);
            }

            if (args.from !== undefined || args.to !== undefined) {
                applied.push([args.from, args.to].filter(Boolean).join(" to "));
            }

            // The page's own controls are multiselects, so several values are a filter, not a
            // conflict: "france and germany" is one Contracts filter with two entries.
            const asList = (value) => (Array.isArray(value) ? value : [value]);

            const pickAll = (available, requested, what) =>
                asList(requested).map((one) => {
                    const found = (available ?? []).find((item) => String(item.id) === String(one));

                    if (found === undefined) {
                        throw new Error("This agency has no " + what + " " + one + ".");
                    }

                    return found;
                });

            if (args.contract !== undefined) {
                const contracts = pickAll(
                    $scope.contractTypesDataManager.getActiveContractTypes(),
                    args.contract,
                    "contract"
                );

                $scope.pageManager.searchParams.selectedContractTypes.value = contracts;
                applied.push(contracts.map((contract) => contract.name).join(" and "));
            }

            // The multiselects below bind straight to the search params, so setting the value is
            // all the page needs - the dropdown label follows from the object.
            if (args.office !== undefined) {
                const offices = pickAll(
                    $scope.officeDataManager.getOffices(),
                    args.office,
                    "office"
                );

                $scope.pageManager.searchParams.selectedOffices.value = offices;
                applied.push(offices.map((office) => office.name).join(" and "));
            }

            if (args.serviceCode !== undefined) {
                const serviceCodes = pickAll(
                    $scope.serviceCodesDataManager.getActiveServiceCodes(),
                    args.serviceCode,
                    "service code"
                );

                $scope.pageManager.searchParams.selectedServiceCodes.value = serviceCodes;
                applied.push(serviceCodes.map((code) => code.name).join(" and "));
            }

            for (const [name, label] of [
                ["invoiceId", "invoice"],
                ["invoiceBatchId", "invoice batch"],
                ["exportBatchId", "export batch"]
            ]) {
                if (args[name] !== undefined) {
                    $scope.pageManager.searchParams[name].value = String(args[name]);
                    applied.push(label + " " + String(args[name]));
                }
            }

            // These are read out of the $scope.selectedX models by loadItems(), so set them there
            // rather than on the search params it is about to overwrite.
            const pick = (options, value) =>
                options.filter((option) => asList(value).some((one) => option.id === String(one)));

            if (args.exportStatus !== undefined) {
                $scope.selectedExportStatuses = pick($scope.exportStatusOptions, args.exportStatus);
                applied.push(
                    $scope.selectedExportStatuses.map((item) => item.label).join(" and ") || String(args.exportStatus)
                );
            }

            if (args.paymentStatus !== undefined) {
                $scope.selectedPaymentStatuses = pick($scope.paymentStatus, args.paymentStatus);
                applied.push(
                    $scope.selectedPaymentStatuses.map((item) => item.label).join(" and ") || String(args.paymentStatus)
                );
            }

            if (args.billType !== undefined) {
                $scope.selectedBillType = pick($scope.billTypeOptions, args.billType);
                applied.push($scope.selectedBillType[0]?.label ?? String(args.billType));
            }

            if (args.manualHold !== undefined) {
                $scope.selectedManualHold = pick($scope.manualHoldOptions, args.manualHold);
                applied.push($scope.selectedManualHold[0]?.label ?? String(args.manualHold));
            }

            if (args.exportType !== undefined) {
                $scope.selectedEBillingProviders = pick($scope.eBillingProviderOptions, args.exportType);

                if ($scope.selectedEBillingProviders.length === 0) {
                    throw new Error("This agency has no export type " + args.exportType + ".");
                }

                applied.push($scope.selectedEBillingProviders[0].label);
            }

            if (args.checkLineStatus !== undefined) {
                $scope.selectedHasCheckLine = pick($scope.hasCheckLineOptions, args.checkLineStatus);
                applied.push($scope.selectedHasCheckLine[0]?.label ?? String(args.checkLineStatus));
            }

            const search = $scope.loadItems();

            if (args.textSearch === undefined) {
                return applied.length === 0
                    ? "Re-ran the claims search"
                    : "Showing claims: " + applied.join(", ");
            }

            const term = String(args.textSearch);

            return search.then(function () {
                $scope.globalFilter.val = term;
                $scope.applyGlobalSearch(term);
                applied.push('"' + term + '"');

                return "Showing claims: " + applied.join(", ");
            });
        });
    });

    $scope.$on("$destroy", releaseInvoicesFilterCapability);

    $scope.clickTableRow = function (item) {
        if (!item) return;

        if ($scope.viewType === "Invoice") {
            $rootScope.openInvoiceModal({ invoiceId: item.id });
        }
        if ($scope.viewType === "Service") {
            $rootScope.openVisitInstanceModal(item.visitInstanceId);
        }
    };

    $scope.openModalToExportInvoices = function () {
        if ($scope.viewType === "Invoice") {
            billingInvoicesProcessor.openInvoicesExporterModalByInvoices(
                $scope.selectionLogic.getSelectedItems().map(item => item.id),
                $scope.loadItems
            );
        }
        if ($scope.viewType === "Service") {
            billingInvoicesProcessor.openInvoicesExporterModalByInvoiceVisits(
                $scope.selectionLogic.getSelectedItems().map(item => item.id),
                $scope.loadItems
            );
        }
    };

    $scope.openModalToForceExportInvoices = function () {
        if ($scope.viewType === "Invoice") {
            billingInvoicesProcessor.openInvoicesForceExporterModalByInvoices(
                $scope.selectionLogic.getSelectedItems().map(item => item.id),
                $scope.loadItems
            );
        }
        if ($scope.viewType === "Service") {
            billingInvoicesProcessor.openInvoicesForceExporterModalByInvoiceVisits(
                $scope.selectionLogic.getSelectedItems().map(item => item.id),
                $scope.loadItems
            );
        }
    };

    $scope.openModalToForceNewExportInvoices = function () {
        if ($scope.viewType === "Invoice") {
            billingInvoicesProcessor.openInvoicesForceNewExporterModalByInvoices(
                $scope.selectionLogic.getSelectedItems().map(item => item.id),
                $scope.loadItems
            );
        }
        if ($scope.viewType === "Service") {
            billingInvoicesProcessor.openInvoicesForceNewExporterModalByInvoiceServices(
                $scope.selectionLogic.getSelectedItems().map(item => item.id),
                $scope.loadItems
            );
        }
    };

    $scope.openModalToRecheckPendingInvoices = function () {
        const invoiceIds = $scope.viewType === "Invoice" ? 
            $scope.selectionLogic.getSelectedItems().map(item => item.id) :
            Array.from(new Set($scope.selectionLogic.getSelectedItems().map(item => item.invoiceId)));

        billingInvoicesProcessor.openRecheckPendingInvoicesModalByInvoices(invoiceIds, $scope.loadItems);
    };

    $scope.openModalToDeleteInvoices = function () {
        if ($scope.viewType === "Invoice") {
            const invoiceIds = [];
            $scope.selectionLogic.getSelectedItems().forEach(function (invoice) {
                invoiceIds.push(invoice.id);
            });

            SweetAlert.swal({
                title: "Delete invoices",
                text: "Are you sure you want to delete the invoices for the " + invoiceIds.length + " selected invoices? ",
                type: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3077EB",
                confirmButtonText: "Yes, remove",
                closeOnConfirm: true,
                closeOnCancel: true
            }, function (isConfirm) {
                if (isConfirm) {
                    DatabaseApi.post('agencies/' + $rootScope.agencyId +
                        '/agency_members/' + $rootScope.agencyMemberId + '/delete_invoices/', { invoiceIds }).then(function (res) {
                            toaster.pop('success', "Success", "Deleted invoices successfully");
                            $scope.loadItems();
                        }, function (err) {
                            toaster.pop('error', "Something went wrong", "Failed to delete invoices");
                        })
                }
            });
        }
    }

    $scope.openModalToUnexportInvoices = function () {
        const invoiceIds = [];
        $scope.selectionLogic.getSelectedItems().forEach(function (invoice) {
            invoiceIds.push(invoice.id);
        });

        if (invoiceIds.length > 0) {
            SweetAlert.swal({
                title: "Unexport invoices",
                text: "Are you sure you want to unexport these " + invoiceIds.length + " selected invoices? ",
                type: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3077EB",
                confirmButtonText: "Yes, unexport",
                closeOnConfirm: true,
                closeOnCancel: true
            }, function (isConfirm) {
                if (isConfirm) {
                    DatabaseApi.post(
                        `agencies/${$rootScope.agencyId}/agency_member/${$rootScope.agencyMemberId}/unexport_invoices`, 
                        { invoiceIds }).then(
                            () => {
                            toaster.pop('success', "Success", "Unexported invoices successfully");
                            $scope.loadItems();
                        }, () => {
                            toaster.pop('error', "Something went wrong", "Failed to unexport invoices");
                        })
                }
            });
        }
    };

    $scope.exportInvoiceAs = function (eBillingType, forceNew) {
        const invoiceVisitIds = [];
        if ($scope.viewType === "Invoice") {
            $scope.selectionLogic.getSelectedItems().forEach((invoice) => {
                if (invoice.invoiceVisitIds !== null) {
                    invoiceVisitIds.push(...invoice.invoiceVisitIds);
                }
            });
        }

        if ($scope.viewType === "Service") {
            $scope.selectionLogic.getSelectedItems().forEach(row => {
                invoiceVisitIds.push(row.id);
            });
        }

        billingInvoicesProcessor.openInvoicesExporterModalByInvoiceServiceAs(invoiceVisitIds, $scope.loadItems, { eBillingType: eBillingType, forceNew: forceNew });
    }

    initialize();

    $scope.applyGlobalSearch = function (term) {
        const filter = { $: term };
        const tableParamsHolder = $scope.viewType === "Invoice" ? $scope.tableView.invoice : $scope.tableView.service;
        if (tableParamsHolder.tableParams) {
            angular.extend(tableParamsHolder.tableParams.filter(), filter);
            const allItems = $filter('filter')(tableParamsHolder.allItems, $scope.globalFilter.val);
            initSelection(allItems, $scope.viewType)
        }
    };

    $scope.getExportInvoiceTableRows = function(selected) {
        const items = selected ? $scope.selectionLogic.getSelectedItems() : $scope.selectionLogic.data.items;
        const rows = [];
        const titles = [
            'Exports',
            'Payment',
            'ID',
            'Date Range',
            'Total Billed Hours',
            'Total Billed',
            'Total Paid',
            'Total Units',
            'Patient',
            'Contract',
            "Office",
            "Service Codes",
            "Service Code IDs",
            'Invoice Batch ID',
            'Number of Visits',
            'Number of Administrative Payments',
            'Number of Services',
            'Created at',
            'Patient Medflyt ID',
            'Patient HHA Exchange ID',
            'Patient Admission ID',
            'Patient SSN',
            'Patient Medicare Number',
            'Patient Medicaid Number',
            'Patient Address',
            'Patient Gender'
        ];
        rows.push(titles);

        // Filter table data by global filter
        const allItems = $filter('filter')(items, $scope.globalFilter.val);

        allItems.forEach(dataRow => {
            const csvRow = [];
            titles.forEach(title => {
                let toPush = "";
                switch (title) {
                    case "Exports":
                        toPush = dataRow.exportedStatus || '';
                        break;
                    case "Payment":
                        toPush = dataRow.paymentStatus || '';
                        break;
                    case "ID":
                        toPush = $rootScope.isFCAgency ? dataRow.id : dataRow.displayId.replace('#', '') || '';
                        break;
                    case "Date Range":
                        toPush = `${$filter("mfShortDate")(dataRow.startDate)} - ${$filter("mfShortDate")(dataRow.endDate)}`;
                        break;
                    case "Total Billed Hours":
                        toPush = $filter("secondsToLabel")(dataRow.totalBilledSeconds);
                        break;
                    case "Total Billed":
                        toPush = $filter("centsToDollars")(dataRow.totalBilled, true);
                        break;
                    case "Total Paid":
                        toPush = $filter("centsToDollars")(dataRow.totalPaid, true);
                        break;
                    case "Total Units":
                        toPush = dataRow.totalUnitsThousandths / 1000;
                        break;
                    case "Patient":
                        toPush = dataRow.patientName || '';
                        break;
                    case "Contract":
                        toPush = dataRow.contractTypeName || '';
                        break;
                    case "Office":
                        toPush = dataRow.officeName || '';
                        break;
                    case "Invoice Batch ID":
                        toPush = dataRow.invoiceBatchDisplayId.replace('#', '') || '';
                        break;
                    case "Number of Visits":
                        toPush = dataRow.visitCount || '';
                        break;
                    case "Number of Administrative Payments":
                        toPush = dataRow.administrativePaymentCount || '';
                        break;
                    case "Number of Services":
                        toPush = dataRow.serviceCount || '';
                        break;
                    case "Created at":
                        toPush = $filter("mfShortTime")(dataRow.createdAt, ['withDate']) || '';
                        break;
                    case "Patient Medflyt ID":
                        toPush = dataRow.patientId || '';
                        break;
                    case "Patient HHA Exchange ID":
                        toPush = dataRow.patientExternalId || '';
                        break;
                    case "Patient Admission ID":
                        toPush = dataRow.patientAdmissionId || '';
                        break;
                    case "Patient SSN":
                        toPush = dataRow.patientSSN || '';
                        break;
                    case "Patient Medicare Number":
                        toPush = dataRow.patientMedicareNumber || '';
                        break;
                    case "Patient Medicaid Number":
                        toPush = dataRow.patientMedicaidNumber || '';
                        break;
                    case "Patient Address":
                        toPush = dataRow.patientAddress || '';
                        break;
                    case "Patient Gender":
                        toPush = dataRow.patientGender || '';
                        break;
                    case "Service Codes":
                        toPush = dataRow.serviceCodes || '';
                        break;
                    case "Service Code IDs":
                        toPush = dataRow.serviceCodeIds || '';
                        break;
                    default:
                        break;
                }

                csvRow.push("\"" + toPush + "\"");
            });
            rows.push(csvRow);
        });

        return rows;
    }

    $scope.getExportServiceTableRows = function(selected) {
        const items = selected ? $scope.selectionLogic.getSelectedItems() : $scope.selectionLogic.data.items;
        const rows = [];
        const titles = [
            'Type',
            'Exports',
            'Payment',
            'Invoice ID',
            'Visit ID',
            'Service Date',
            'Total Billed Hours',
            'Total Billed',
            'Total Paid',
            'Total Units',
            'Patient',
            'Contract',
            "Office",
            "Service Code",
            "Service Code ID",
            'Invoice Batch ID',
            'Created at',
            'Patient Medflyt ID',
            'Patient HHA Exchange ID',
            'Patient Admission ID',
            'Patient SSN',
            'Patient Medicare Number',
            'Patient Medicaid Number',
            'Patient Address',
            'Patient Gender'
        ];
        rows.push(titles);

        // Filter table data by global filter
        const allItems = $filter('filter')(items, $scope.globalFilter.val);

        allItems.forEach(dataRow => {
            const csvRow = [];
            titles.forEach(title => {
                let toPush = "";
                switch (title) {
                    case "Type":
                        toPush = dataRow.serviceType || '';
                        break;
                    case "Exports":
                        toPush = dataRow.exportedStatus || '';
                        break;
                    case "Payment":
                        toPush = dataRow.paymentStatus || '';
                        break;
                    case "Invoice ID":
                        toPush = dataRow.displayId.replace('#', '') || '';
                        break;
                    case "Visit ID":
                        toPush = dataRow.visitInstanceId || '';
                        break;
                    case "Service Date":
                        toPush = $filter("mfShortDate")(dataRow.serviceDate);
                        break;
                    case "Total Billed Hours":
                        toPush = $filter("secondsToLabel")(dataRow.totalBilledSeconds);
                        break;
                    case "Total Billed":
                        toPush = $filter("centsToDollars")(dataRow.totalBilled, true);
                        break;
                    case "Total Paid":
                        toPush = $filter("centsToDollars")(dataRow.totalPaid, true);
                        break;
                    case "Total Units":
                        toPush = dataRow.totalUnitsThousandths / 1000;
                        break;
                    case "Patient":
                        toPush = dataRow.patientName || '';
                        break;
                    case "Contract":
                        toPush = dataRow.contractTypeName || '';
                        break;
                    case "Office":
                        toPush = dataRow.officeName || '';
                        break;
                    case "Invoice Batch ID":
                        toPush = dataRow.invoiceBatchDisplayId.replace('#', '') || '';
                        break;
                    case "Created at":
                        toPush = $filter("mfShortTime")(dataRow.createdAt, ['withDate']) || '';
                        break;
                    case "Patient Medflyt ID":
                        toPush = dataRow.patientId || '';
                        break;
                    case "Patient HHA Exchange ID":
                        toPush = dataRow.patientExternalId || '';
                        break;
                    case "Patient Admission ID":
                        toPush = dataRow.patientAdmissionId || '';
                        break;
                    case "Patient SSN":
                        toPush = dataRow.patientSSN || '';
                        break;
                    case "Patient Medicare Number":
                        toPush = dataRow.patientMedicareNumber || '';
                        break;
                    case "Patient Medicaid Number":
                        toPush = dataRow.patientMedicaidNumber || '';
                        break;
                    case "Patient Address":
                        toPush = dataRow.patientAddress || '';
                        break;
                    case "Patient Gender":
                        toPush = dataRow.patientGender || '';
                        break;
                    case "Service Code":
                        toPush = dataRow.serviceCode || '';
                        break;
                    case "Service Code ID":
                        toPush = dataRow.serviceCodeId || '';
                        break;
                    default:
                        break;
                }

                csvRow.push("\"" + toPush + "\"");
            });
            rows.push(csvRow);
        });

        return rows;
    }

    $scope.exportTable = function (selected) {
        $scope.loadingCSV = true;

        const rows = $scope.viewType === "Invoice" ? $scope.getExportInvoiceTableRows(selected) : $scope.getExportServiceTableRows(selected);

        let csvContent = "";
        rows.forEach(function (rowArray) {
            const row = rowArray.join(",");
            csvContent += row + "\r\n";
        });

        const encodedUri = "data:text/csv;charset=utf-8," + encodeURIComponent(universalBOM+csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", getExportedFileName());
        document.body.appendChild(link);

        link.click();
        $scope.loadingCSV = false;
    };

    $scope.handleEntitySelection = (type, id) => {
        $scope.selectedEntity.type = type;
        $scope.selectedEntity.id = id;
        $scope.pageManager.updateSearchParamValue("selectedPatientId", id);
    }

    $scope.handleEntityDeselect = () => {
        $scope.selectedEntity.id = null;
        $scope.selectedEntity.type = null;
        $scope.pageManager.updateSearchParamValue("selectedPatientId", null);
    }

    const getExportedFileName = function () {
        const filename = "medflyt-invoices"
            + $filter("date")(new Date($scope.pageManager.searchParams.from.value), "yyyy-MM-dd")
            + "-to-"
            + $filter("date")(new Date($scope.pageManager.searchParams.to.value), "yyyy-MM-dd")
            + ".csv";
        return filename;
    }

    $scope.manualHoldExtraSetting = {
        styleActive: true,
        showCheckAll: false,
        showUncheckAll: false,
        enableSearch: true,
    };

    $scope.hasCheckLineExtraSetting = {
        styleActive: true,
        showCheckAll: false,
        showUncheckAll: false,
        enableSearch: false,
    };

    $scope.billTypeExtraSetting = {
        styleActive: true,
        showCheckAll: false,
        showUncheckAll: false,
        enableSearch: true,
    };
};
